import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "field with-icon openingTimes-row" }
const _hoisted_2 = { class: "opening-times-inputs" }
const _hoisted_3 = { class: "control select-input" }
const _hoisted_4 = { class: "toggle-container" }
const _hoisted_5 = { class: "control" }
const _hoisted_6 = { class: "control inline-item toggle-label" }
const _hoisted_7 = {
  key: 0,
  class: "toggle-label__text--online"
}
const _hoisted_8 = {
  key: 1,
  class: "toggle-label__text--offline"
}
const _hoisted_9 = { class: "time-inputs" }
const _hoisted_10 = { class: "control time-input" }
const _hoisted_11 = {
  key: 0,
  class: "control inline-item"
}
const _hoisted_12 = { class: "control time-input" }
const _hoisted_13 = { class: "inline-item delete-icon" }

import { ref, watch } from 'vue';
import useVuelidate, { ValidationRule } from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { BusinessHoursOption, BusinessHoursOptions } from '.';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import DateTimeService from '@shared/services/dateTime';
import VuelidateUtil from '@shared/util/vuelidateUtil';

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'BusinessHoursRow',
  props: {
    modelValue: {},
    options: {},
    showDeleteIcon: { type: Boolean },
    holidays: {}
  },
  emits: ["update:modelValue", "delete"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Two-way binding for the row
const row = ref(props.modelValue);
watch(
  () => props.modelValue,
  (newVal: BusinessHoursOptions) => {
    row.value = newVal;
  },
);
watch(row, () => emit('update:modelValue', row.value), { deep: true });

// Validation rules
const time = (value: string) => !row.value.open || !value || DateTimeService.isValidTime(value);
const after = (prop: keyof BusinessHoursOption) =>
  helpers.withMessage(`End time must be after ${prop}`, (value: string) => {
    if (!value || !row.value.open) return true;
    const compareTo = row.value[prop];
    if (!compareTo || typeof compareTo !== 'string') return true;
    const minutes = DateTimeService.getMinutes(value);
    const compareToMinutes = DateTimeService.getMinutes(compareTo)!;
    return minutes !== undefined && minutes > compareToMinutes;
  });

const rules = {
  startTime: {
    required: requiredIf(() => row.value.open),
    time,
  },
  endTime: {
    required: requiredIf(() => row.value.open),
    time,
    after: after('startTime'),
  },
};

const $v = useVuelidate(rules, row);

// Error message generation
function getErrorMessage(field: 'startTime' | 'endTime', label: string): string | null {
  return VuelidateUtil.getErrorMessage($v, field, label, (validationRule: ValidationRule) => {
    if (field === 'endTime' && VuelidateUtil.isValidationInvalid($v, field, 'after')) return 'Must be after start time';
    if (VuelidateUtil.isValidationInvalid($v, field, 'time')) return 'Must be in HH:mm format';
    return '';
  });
}

// Handlers
function onRowChange() {
  $v.value.$touch();
  emit('update:modelValue', row.value);
}

function deleteRow() {
  emit('delete');
}

return (_ctx: any,_cache: any) => {
  const _component_n_select = _resolveComponent("n-select")!
  const _component_n_toggle = _resolveComponent("n-toggle")!
  const _component_n_text_input = _resolveComponent("n-text-input")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_n_select, {
          modelValue: row.value.day,
          "onUpdate:modelValue": [
            _cache[0] || (_cache[0] = ($event: any) => ((row.value.day) = $event)),
            onRowChange
          ],
          options: __props.options,
          label: "Select a day",
          "label-prop": "name",
          "value-prop": "name",
          "disabled-prop": "disabled",
          "hide-label": true
        }, null, 8, ["modelValue", "options"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_n_toggle, {
            modelValue: row.value.open,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((row.value.open) = $event)),
            label: "Toggle Online/Offline",
            "hide-label": true,
            onInput: onRowChange
          }, null, 8, ["modelValue"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          (row.value.open)
            ? (_openBlock(), _createElementBlock("span", _hoisted_7, "online"))
            : _createCommentVNode("", true),
          (!row.value.open)
            ? (_openBlock(), _createElementBlock("span", _hoisted_8, "offline"))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_9, [
        _withDirectives(_createElementVNode("div", _hoisted_10, [
          _createVNode(_component_n_text_input, {
            type: "text",
            modelValue: row.value.startTime,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((row.value.startTime) = $event)),
            label: "Start time",
            "hide-label": true,
            "error-message": getErrorMessage('startTime', 'Start time'),
            onInput: onRowChange
          }, null, 8, ["modelValue", "error-message"])
        ], 512), [
          [_vShow, row.value.open]
        ]),
        (row.value.open)
          ? (_openBlock(), _createElementBlock("span", _hoisted_11, "to"))
          : _createCommentVNode("", true),
        _withDirectives(_createElementVNode("div", _hoisted_12, [
          _createVNode(_component_n_text_input, {
            type: "text",
            modelValue: row.value.endTime,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((row.value.endTime) = $event)),
            label: "End time",
            "hide-label": true,
            "error-message": getErrorMessage('endTime', 'End time'),
            onInput: onRowChange
          }, null, 8, ["modelValue", "error-message"])
        ], 512), [
          [_vShow, row.value.open]
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      (__props.showDeleteIcon)
        ? (_openBlock(), _createElementBlock("svg", {
            key: 0,
            onClick: deleteRow,
            class: "add-icon"
          }, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("use", {
              "xmlns:xlink": "http://www.w3.org/1999/xlink",
              "xlink:href": "#trash"
            }, null, -1)
          ]))]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})